/**
 * @file quantity.method.ts
 * @module utils/quantity.method
 * @description Utility methods for quantity calculations and formatting.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { WarehouseItem } from "../app/context/WarehouseContext";
import { VariantWithPriceCatalog } from "../types";
import {
  QuantityTypes,
  QuantityTypesLabel,
  QuantityTypeSmallCase,
} from "./common.constants";

/** Validate that both string values parse as numbers greater than 0 */
export const ValidateNum = <T extends string>(a: T, b: T): boolean => {
  const num1 = parseFloat(a);
  const num2 = parseFloat(b);
  if (isNaN(num1) || num1 <= 0 || isNaN(num2) || num2 <= 0) return false;
  return true;
};

/** `getQuantityTypeLabel` method used for getting `Quantity Type` according to the number */
export const getQuantityTypeLabel = (quantityType: number) => {
  if (quantityType === QuantityTypes.CartonWithDozens) return "CWD";
  if (quantityType === QuantityTypes.CartonWithPieces) return "CWP";
  if (quantityType === QuantityTypes.Dozen) return "DWP";
  if (quantityType === QuantityTypes.Piece) return "P";
  return "Unknown";
};

/** `getQuantityTypeLabelViaSmallCase` method used for getting `label` name via small case */
export const getQuantityTypeLabelViaSmallCase = (
  key: QuantityTypeSmallCase,
) => {
  switch (key) {
    case "piece":
      return QuantityTypesLabel[QuantityTypes.Piece];
    case "dozen":
      return QuantityTypesLabel[QuantityTypes.Dozen];
    case "cwp":
      return QuantityTypesLabel[QuantityTypes.CartonWithPieces];
    case "cwd":
      return QuantityTypesLabel[QuantityTypes.CartonWithDozens];
    default:
      return "Unknown";
  }
};

export const getActivePrice = (
  variantItem: VariantWithPriceCatalog,
  type: number,
  selectedWarehouse: WarehouseItem,
) => {
  const pricingArr =
    variantItem?.priceCatalog?.pricing?.filter((p) => p.active) || [];
  const whPricing = pricingArr.find(
    (p) => p.warehouseId === selectedWarehouse?._id,
  );
  const qt = whPricing?.quantityTypes?.find(
    (q) => Number(q.type) === Number(type),
  );

  if (qt) return { cp: qt.costPrice || 0, sp: qt.sellingPrice || 0 };

  const actualVariant = variantItem?.variant;
  const fallback = actualVariant?.quantityTypes?.find(
    (q: any) => Number(q.type) === Number(type),
  );
  return { cp: 0, sp: fallback?.amount || 0 };
};

/** Method for calculating gross profit margin */
export const calculateGPMargin = (cp: number, sp: number) => {
  if (!sp || sp === 0) return "0.0%";
  if (!cp || cp === 0) return "0.0%";

  const margin = ((sp - cp) / sp) * 100;
  return `${margin.toFixed(1)}%`;
};
